import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Search, 
  Eye,
  ClipboardList,
  MapPin,
  Calendar,
  Banknote,
  Home,
  User,
  Phone,
  MessageCircle
} from "lucide-react";

export default function AdminTenantInfoPage() {
  const { token } = useAuth();
  const [tenantInfoList, setTenantInfoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewingInfo, setViewingInfo] = useState(null);

  useEffect(() => {
    fetchTenantInfo();
  }, []);

  const fetchTenantInfo = async () => {
    try {
      const response = await axios.get(`${API}/admin/tenant-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantInfoList(response.data);
    } catch (error) {
      toast.error("Failed to load tenant information");
    } finally {
      setLoading(false);
    }
  };

  const filteredInfo = tenantInfoList.filter(info => 
    info.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    info.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    info.house_location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    info.user_full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return `₦${amount}`;
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-tenant-info-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary">Tenant Information Update</h1>
              <p className="text-text-muted mt-1">View submitted tenant information forms</p>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                data-testid="search-tenant-info-input"
                placeholder="Search by name or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-white border-stone-200"
              />
            </div>
          </div>

          {/* Tenant Info Cards */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg">Submitted Forms ({filteredInfo.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : filteredInfo.length === 0 ? (
                <div className="text-center py-8">
                  <ClipboardList className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-text-muted">No tenant information forms submitted yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredInfo.map((info) => (
                    <div 
                      key={info.id} 
                      className="bg-stone-50 rounded-xl p-4 border border-stone-200 hover:shadow-md transition-shadow"
                      data-testid={`tenant-info-card-${info.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-primary">
                              {info.last_name} {info.first_name}
                            </h3>
                            {info.user_full_name && (
                              <p className="text-xs text-text-muted">@{info.username}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex items-center gap-2 text-text-muted">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span>{info.house_location} - Apt #{info.apartment_number}</span>
                        </div>
                        <div className="flex items-center gap-2 text-text-muted">
                          <Calendar className="w-4 h-4 flex-shrink-0" />
                          <span>Living: {info.duration_living}</span>
                        </div>
                        <div className="flex items-center gap-2 text-text-muted">
                          <Banknote className="w-4 h-4 flex-shrink-0" />
                          <span>Last Payment: {formatCurrency(info.last_payment_amount)}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                          {info.payment_frequency}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setViewingInfo(info)}
                          data-testid={`view-info-btn-${info.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>

                      {(info.amount_in_arrears && info.amount_in_arrears !== "0") && (
                        <div className="mt-3 pt-3 border-t border-stone-200">
                          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                            Arrears: {formatCurrency(info.amount_in_arrears)}
                          </Badge>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* View Details Dialog */}
      <Dialog open={!!viewingInfo} onOpenChange={() => setViewingInfo(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Tenant Information Details</DialogTitle>
            <DialogDescription>
              Complete information submitted by the tenant
            </DialogDescription>
          </DialogHeader>
          
          {viewingInfo && (
            <div className="space-y-6 py-4">
              {/* Personal Info */}
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-primary">Personal Information</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">Last Name:</span>
                    <p className="font-medium">{viewingInfo.last_name}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">First Name:</span>
                    <p className="font-medium">{viewingInfo.first_name}</p>
                  </div>
                  {viewingInfo.user_full_name && (
                    <div className="col-span-2">
                      <span className="text-text-muted">Account Name:</span>
                      <p className="font-medium">{viewingInfo.user_full_name} (@{viewingInfo.username})</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              {(viewingInfo.mobile_phone || viewingInfo.whatsapp_phone) && (
                <div className="bg-stone-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Phone className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold text-primary">Contact Information</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {viewingInfo.mobile_phone && (
                      <div>
                        <span className="text-text-muted flex items-center gap-1">
                          <Phone className="w-3 h-3" /> Mobile Tel:
                        </span>
                        <p className="font-medium">{viewingInfo.mobile_phone}</p>
                      </div>
                    )}
                    {viewingInfo.whatsapp_phone && (
                      <div>
                        <span className="text-text-muted flex items-center gap-1">
                          <MessageCircle className="w-3 h-3 text-green-600" /> WhatsApp:
                        </span>
                        <p className="font-medium text-green-700">{viewingInfo.whatsapp_phone}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Location Info */}
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Home className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-primary">Location Details</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">House Location:</span>
                    <p className="font-medium">{viewingInfo.house_location}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Apartment #:</span>
                    <p className="font-medium">{viewingInfo.apartment_number}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-text-muted">Duration Living:</span>
                    <p className="font-medium">{viewingInfo.duration_living}</p>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Banknote className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-primary">Payment Details</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-text-muted">Payment Frequency:</span>
                    <p className="font-medium">{viewingInfo.payment_frequency}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Last Payment Amount:</span>
                    <p className="font-medium">{formatCurrency(viewingInfo.last_payment_amount)}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Date of Last Payment:</span>
                    <p className="font-medium">{formatDate(viewingInfo.last_payment_date)}</p>
                  </div>
                  <div>
                    <span className="text-text-muted">Date of Next Payment:</span>
                    <p className="font-medium">{formatDate(viewingInfo.next_payment_date)}</p>
                  </div>
                </div>
              </div>

              {/* Arrears Info */}
              {(viewingInfo.months_years_in_arrears || viewingInfo.amount_in_arrears) && (
                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                  <h3 className="font-semibold text-red-800 mb-4">Arrears Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-red-700">Months/Years in Arrears:</span>
                      <p className="font-medium text-red-900">{viewingInfo.months_years_in_arrears || "-"}</p>
                    </div>
                    <div>
                      <span className="text-red-700">Amount in Arrears:</span>
                      <p className="font-medium text-red-900">{formatCurrency(viewingInfo.amount_in_arrears) || "-"}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submitted Info */}
              <div className="text-xs text-text-muted text-center">
                {viewingInfo.submitted_at && (
                  <p>Submitted: {formatDate(viewingInfo.submitted_at)}</p>
                )}
                {viewingInfo.updated_at && (
                  <p>Last Updated: {formatDate(viewingInfo.updated_at)}</p>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t border-stone-200">
                <Button variant="outline" onClick={() => setViewingInfo(null)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
